import { useCallback, useEffect, useRef } from 'react';

import { useValidateCohortDefinition } from '@verantos-dev/app-state-cohort-builder';
import { type CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import { useConceptGroupsMaps } from '../../hooks/useConceptGroupsMaps';

/** Optional callback invoked with the parsed cohort definition after the `cohort_dsl` event is processed. */
export type OnCohortDslReceived = (definition: CohortBuilderDefinitionLegacy) => void;

export const useCohortDefinitionListeners = (
  onCohortDslReceived?: OnCohortDslReceived,
  onPartialCohortDslReceived?: (dsl: Record<string, unknown>) => void,
  onClearConceptGroupConfidence?: () => void
) => {
  const onCohortDslReceivedRef = useRef(onCohortDslReceived);
  onCohortDslReceivedRef.current = onCohortDslReceived;
  const onPartialCohortDslReceivedRef = useRef(onPartialCohortDslReceived);
  onPartialCohortDslReceivedRef.current = onPartialCohortDslReceived;
  const onClearConceptGroupConfidenceRef = useRef(onClearConceptGroupConfidence);
  onClearConceptGroupConfidenceRef.current = onClearConceptGroupConfidence;

  const { socket: currentSocket } = useResearchAssistantSocketContext();
  const { transformCohortDefinition } = useValidateCohortDefinition();
  const { _mapConceptGroupNameToId } = useConceptGroupsMaps();

  const cohortDslHandler = useCallback(
    (message: unknown) => {
      if (!message) return;

      try {
        // Parse and transform message assuming JSON payloads by default.
        let parsedMessage = message;
        let isParsedJson = false;

        if (typeof parsedMessage === 'string') {
          const mapped = _mapConceptGroupNameToId(parsedMessage);
          try {
            parsedMessage = JSON.parse(mapped);
            isParsedJson = true;
          } catch {
            parsedMessage = mapped;
          }
        } else if (parsedMessage && typeof parsedMessage === 'object') {
          isParsedJson = true;
        }

        if (isParsedJson) {
          parsedMessage = transformCohortDefinition(
            parsedMessage as CohortBuilderDefinitionLegacy,
            { addIds: true, dirty: true }
          );
          localStorage.setItem('cohortJson', JSON.stringify(parsedMessage, null, 2));
        }

        // Store DSL (string or object) in localStorage
        const isStringMessage = typeof parsedMessage === 'string';
        localStorage.setItem(
          'cohortDsl',
          isStringMessage ? (parsedMessage as string) : JSON.stringify(parsedMessage)
        );

        let _message = parsedMessage;

        // Replace fancy quotes only if string
        if (typeof _message === 'string') {
          const regSQ = new RegExp("['''`´]", 'g');
          _message = _message.replace(regSQ, "'");
        }

        // Store post-mapping DSL
        localStorage.setItem('cohortDsl_after', JSON.stringify(_message));

        console.info('Generating UI model from Research Assistant DSL');

        // Clear concept groups confidence via callback
        onClearConceptGroupConfidenceRef.current?.();

        // Notify caller — caller owns hydrating state (Zustand or Redux) from the definition
        if (isParsedJson) {
          onCohortDslReceivedRef.current?.(_message as CohortBuilderDefinitionLegacy);
        }
      } catch (e) {
        console.error('Failed to process cohort DSL:', e);
      }
    },
    [_mapConceptGroupNameToId, transformCohortDefinition]
  );

  const partialCohortDslHandler = useCallback((message: unknown) => {
    if (!message) return;

    try {
      const parsed = typeof message === 'string' ? JSON.parse(message) : message;
      onPartialCohortDslReceivedRef.current?.(parsed as Record<string, unknown>);
    } catch (e) {
      console.warn('[partial_cohort_dsl] Failed:', e);
    }
  }, []);

  useEffect(() => {
    if (currentSocket) {
      currentSocket.on('cohort_dsl', cohortDslHandler);
      currentSocket.on('partial_cohort_dsl', partialCohortDslHandler);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off('cohort_dsl', cohortDslHandler);
        currentSocket.off('partial_cohort_dsl', partialCohortDslHandler);
      }
    };
  }, [cohortDslHandler, partialCohortDslHandler, currentSocket]);
};
